import { DragIndicatorRounded } from "@mui/icons-material";
import { Box, Stack, useTheme } from "@mui/material";
import { useState, useEffect, useLayoutEffect, useRef, useCallback } from "react";
import { useDispatch } from "react-redux";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import BlockPicker from "./BlockPicker";
import InlineToolbar from "./InlineToolbar";
import CommonBlock from "./CommonBlock";
import { useSelector } from "react-redux";
import ImageGallery from "../../themebuilder/ImageGallery";




const FlexibleEditor = ({
    blocks,
    onChange,
    isSiteBuilder = false,
    onOpenGallery: onOpenGalleryProp = () => { },
    editingBlockId,
    setEditingBlockId,
    editingCardId: externalEditingCardId,
    setEditingCardId: setExternalEditingCardId,
    disableBlockActions = false,
    hideBlockDelete = false,
    hidePrompt = false,
    nestedDelete = false,
    allowEmpty = false,
    droppableId = "flexible-editor-blocks",
}) => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const { reducer } = useSelector((state) => state);
    const { pageJson, siteBuilderSelectedChild, siteBuilderHoveredChild, updateComponent } = reducer;
    const [activeBlockId, setActiveBlockId] = useState(null);
    const [localEditingCardId, _setLocalEditingCardId] = useState(() => {
        try { return sessionStorage.getItem('site-builder-editing-card-id'); } catch (e) { return null; }
    });

    const setEditingCardId = useCallback((id) => {
        const normalizedId = (id === null || id === undefined) ? null : String(id);
        if (setExternalEditingCardId) {
            setExternalEditingCardId(normalizedId);
        } else {
            _setLocalEditingCardId(normalizedId);
            if (normalizedId) sessionStorage.setItem('site-builder-editing-card-id', normalizedId);
            else sessionStorage.removeItem('site-builder-editing-card-id');
        }
    }, [setExternalEditingCardId]);

    const editingCardId = (externalEditingCardId !== undefined && externalEditingCardId !== null) ? externalEditingCardId : localEditingCardId;

    const [pickerState, setPickerState] = useState({
        open: false,
        anchorEl: null,
        blockId: null
    });
    const [galleryOpen, setGalleryOpen] = useState(false);
    const [editingBlockIdLocal, setEditingBlockIdLocal] = useState(null);
    const [editingCardIdLocal, setEditingCardIdLocal] = useState(null);
    const containerRef = useRef(null);
    const updateSeqRef = useRef(0);

    
    const [localBlocks, setLocalBlocks] = useState(blocks);

    // Keep localBlocks in sync when Redux changes from OUTSIDE
    const lastExternalBlocksRef = useRef(null);
    const isSplittingRef = useRef(false);
    useEffect(() => {
        if (isSplittingRef.current) return;
        const blocksStr = JSON.stringify(blocks);
        if (lastExternalBlocksRef.current !== blocksStr) {
            lastExternalBlocksRef.current = blocksStr;
            setLocalBlocks(JSON.parse(JSON.stringify(blocks || [])));
        }
    }, [blocks]);

    const pendingFocusRef = useRef(null);

    useLayoutEffect(() => {
        const targetId = pendingFocusRef.current;
        if (!targetId || !isSiteBuilder) return;

        const tryFocus = () => {
            const el = document.getElementById(`block-${targetId}`);
            if (!el) return;
            pendingFocusRef.current = null;
            el.focus();
            try {
                const sel = window.getSelection();
                if (sel) {
                    const range = document.createRange();
                    if (el.childNodes.length > 0) {
                        range.setStart(el.childNodes[0], 0);
                    } else {
                        range.setStart(el, 0);
                    }
                    range.collapse(true);
                    sel.removeAllRanges();
                    sel.addRange(range);
                }
            } catch (err) { }
        };

        tryFocus();
        if (pendingFocusRef.current === targetId) {
            requestAnimationFrame(tryFocus);
        }
    });

    const syncToParent = useCallback((updated) => {
        if (typeof window !== 'undefined' && window.localStorage?.getItem('siteBuilderDebug') === '1') {
            updateSeqRef.current += 1;
            const cardBlocks = (updated || [])
                .filter((b) => b?.type === 'cardgrid')
                .map((b) => ({
                    id: b.id,
                    variant: b.variant,
                    imagePosition: b.cards?.[0]?.imagePosition,
                    cardCount: b.cards?.length || 0
                }));
            console.log('[siteBuilderDebug][FlexibleEditor] syncToParent', {
                seq: updateSeqRef.current,
                blockCount: updated?.length || 0,
                cardBlocks
            });
        }
        lastExternalBlocksRef.current = JSON.stringify(updated);
        onChange(updated);
    }, [onChange]);

    const handleUpdateBlock = useCallback((blockId, updates) => {
        setLocalBlocks((prevBlocks) => {
            const updated = prevBlocks.map((b) => {
                if (String(b.id) !== String(blockId)) return b;
                if (typeof updates === 'function') {
                    const next = updates(b);
                    if (next && typeof next === 'object' && !Array.isArray(next)) {
                        return { ...b, ...next };
                    }
                    return b;
                }
                if (typeof updates === 'string') return { ...b, value: updates };
                return { ...b, ...updates };
            });
            syncToParent(updated);
            return updated;
        });
    }, [syncToParent]);

    const handleAddBlock = (afterBlockId, options = {}) => {
        const { type = 'text', variant = 'body1', value = '', ...rest } = options;
        const newId = Date.now();

        // Filter out picker-only properties
        const { label, icon, hint, category, ...cleanRest } = rest;

        const newUid = `block-${newId}-${Math.floor(Math.random() * 100000)}`;
        const newBlock = { id: newId, uid: newUid, type, variant, value, ...cleanRest };

        if (type === 'text' && cleanRest?.blockStyle === 'quote' && !value) {
            newBlock.value = '<div class="quote-title">Item</div><div class="quote-description">Description</div>';
            newBlock.quoteLayout = 'classic';
        }
        if (type === 'button') {
            newBlock.label = "Button";
            newBlock.url = "#";
            newBlock.props = {
                variant: 'contained',
                sx: {
                    backgroundColor: '#1E40AF',
                    borderRadius: '8px',
                    padding: '10px 24px',
                },
                style: {
                    color: '#ffffff',
                    fontWeight: 600,
                }
            };
        }

        if (type === 'flexible-button') {
            newBlock.label = "Button";
            newBlock.url = "#";
            newBlock.props = {
                variant: 'contained',
                sx: {

                },
                style: {

                }
            };
        }

        if (type === 'flexible-genericCard') {
            newBlock.label = 'Category';
            newBlock.content = {
                title: 'Card Title',
                content: 'Describe your feature or service here.',
                icon: 'star'
            };
            newBlock.props = {
                sx: {
                    borderRadius: '24px',
                    padding: '16px',
                    textAlign: 'center'
                },
                style: {
                    fontWeight: 600
                }
            };
        }

        if (type === 'flexible-content') {
            if (variant === '1') {
                newBlock.content = {
                    title: 'Content Title',
                    content: 'Add your content description or body text here to engage your audience.',
                    icon: 'menu'
                };
            } else if (variant === '2') {
                newBlock.content = {
                    title: 'Content Title',
                    content: 'Add your content description here.',
                    icon: 'menu',
                    src: 'https://images.unsplash.com/photo-1498050108023-c5249f4df085?auto=format&fit=crop&q=80&w=300&h=300'
                };
            } else if (variant === '3') {
                newBlock.content = {
                    title: 'Horizontal Title',
                    content: 'Horizontal content description.',
                    icon: 'menu'
                };
                newBlock.url = '#';
            }
            newBlock.props = {
                sx: {
                    padding: '16px',
                }
            };
        }

        if (type === 'flexible-featureCard') {
            newBlock.content = {
                content: 'Hover over me to remove the shadow!',
                icon: 'account'
            };
            newBlock.props = {
                fontSize: 'medium',
                borderRadius: 'Medium',
                style1: { fontSize: 20, fontWeight: 700 },
                style2: { color: '#ffffff' },
                style3: { color: '#fce4ec' }, 
                style4: { color: '#e91e63' }, 
                style5: { textcolor: '#1a237e' }
            };
        }

        if (type === 'flexible-spacer') {
            newBlock.props = {
                height: '40px'
            };
        }

        if (type === 'flexible-icon') {
            newBlock.children = 'star';
            newBlock.props = {
                fontSize: '24px',
                color: 'inherit',
                sx: { padding: '8px' }
            };
        }

        if (type === 'flexible-dropdown') {
            newBlock.label = 'Dropdown';
            newBlock.children = [
                { label: 'Item 1', url: '#' },
                { label: 'Item 2', url: '#' }
            ];
            newBlock.props = {
                variant: 'text',
                fontSize: 'inherit',
                color: 'inherit'
            };
        }

        if (type === 'flexible-form') {
            newBlock.props = {
                form_id: '',
                buttonLabel: 'Submit'
            };
        }

        if (type === 'table') { newBlock.data = { rows: [['Row 1 Col 1', 'Row 1 Col 2'], ['Row 2 Col 1', 'Row 2 Col 2']] }; }
        if (type === 'accordion') {
            newBlock.emoji = "✨";
            newBlock.title = "New Category";
            newBlock.blocks = [];
        }
        if (type === 'columns') {
            const n = Number(variant) || 2;
            newBlock.numCols = n;
            newBlock.colGap = 2;
            newBlock.columns = Array.from({ length: n }, (_, i) => ({
                id: Date.now() + i + 1,
                blocks: []
            }));
        }
        if (type === 'statgrid') {
            newBlock.displayType = 'number';
            newBlock.statCols = 2;
            newBlock.accentColor = null;
            newBlock.stats = [
                { id: Date.now() + 1, value: 75, maxValue: 100, label: 'Score', description: 'Description' },
                { id: Date.now() + 2, value: 45, maxValue: 100, label: 'Growth', description: 'Description' },
                { id: Date.now() + 3, value: 92, maxValue: 100, label: 'Rating', description: 'Description' },
                { id: Date.now() + 4, value: 60, maxValue: 100, label: 'Progress', description: 'Description' },
            ];
        }
        if (type === 'testimonialgrid') {
            newBlock.layout = 'card';
            newBlock.cols = 2;
            newBlock.showImage = true;
            newBlock.showRating = true;
            newBlock.accentColor = null;
            newBlock.items = [
                { id: Date.now() + 1, quote: 'This product completely transformed how our team works. Absolutely love it!', name: 'Sarah Johnson', role: 'Head of Design, Acme Inc.', image: '', rating: 5 },
                { id: Date.now() + 2, quote: 'Incredible results in just a few weeks. Our productivity doubled and the team is happier.', name: 'Michael Chen', role: 'CTO, Startup Labs', image: '', rating: 5 },
            ];
        }
        if (type === 'card') {
            const baseId = Date.now();
            newBlock.type = 'cardgrid';
            newBlock.cards = [
                { id: baseId + 1, title: 'Card Title', description: 'Card description goes here.' },
            ];
            newBlock.variant = 'grid-3'; // Default for single card
            newBlock.display = 'card';
        }

        if (type === 'cardgrid') {
            const baseId = Date.now();
            const cardCount = (variant === 'featured-block') ? 5 : 3;
            newBlock.cards = Array.from({ length: cardCount }, (_, idx) => ({
                id: baseId + idx + 1,
                title: idx === 0 && variant === 'featured-block' ? 'Featured Story' : 'Card Title',
                description: idx === 0 && variant === 'featured-block'
                    ? 'Lead with a prominent story and support it with secondary cards.'
                    : 'Card description goes here.',
                ...(options.imagePosition ? { imagePosition: options.imagePosition } : {}),
            }));
            newBlock.variant = variant || 'grid-3';
        }

        const updated = [...localBlocks];
        if (afterBlockId) {
            const bIdx = updated.findIndex(b => String(b.id) === String(afterBlockId));
            if (bIdx > -1) {
                if (updated[bIdx].type === 'text' && !updated[bIdx].value && type !== 'text') {
                    updated[bIdx] = newBlock;
                } else {
                    updated.splice(bIdx + 1, 0, newBlock);
                }
            } else {
                updated.push(newBlock);
            }
        } else {
            updated.push(newBlock);
        }

        setLocalBlocks(updated);
        syncToParent(updated);
        if (type === 'text' || type === 'flexible-button') {
            pendingFocusRef.current = String(newId);
            setActiveBlockId(String(newId));
        }
        return newId;
    };

    const handleDeleteBlock = (blockId) => {
        const updated = localBlocks.filter(b => String(b.id) !== String(blockId));
        if (!allowEmpty && updated.length === 0 && !isSiteBuilder) {
            updated.push({ id: Date.now(), type: 'text', value: '', variant: 'body1' });
        }
        if (String(activeBlockId) === String(blockId)) {
            setActiveBlockId(null);
        }
        setLocalBlocks(updated);
        syncToParent(updated);
    };

    const handleDuplicateBlock = useCallback((blockId) => {
        const bIdx = localBlocks.findIndex((b) => String(b.id) === String(blockId));
        if (bIdx === -1) return;
        const source = localBlocks[bIdx];
        const cloned = JSON.parse(JSON.stringify(source));
        cloned.id = Date.now();
        cloned.uid = `block-${cloned.id}-${Math.floor(Math.random() * 100000)}`;
        if (cloned.type === 'customcomponent' && cloned.customItem) {
            cloned.customItem.uid = `cc-${Date.now()}-${Math.floor(Math.random() * 100000)}`;
        }
        const updated = [...localBlocks];
        updated.splice(bIdx + 1, 0, cloned);
        setLocalBlocks(updated);
        syncToParent(updated);
        setActiveBlockId(String(cloned.id));
    }, [localBlocks, syncToParent]);

    const handleSplitBlock = (blockId) => {
        const selection = window.getSelection();
        const bIdx = localBlocks.findIndex(b => String(b.id) === String(blockId));
        if (bIdx === -1) return;

        const currentBlock = localBlocks[bIdx];
        let beforeText = currentBlock.value || "";
        let afterText = "";

        if (selection && selection.rangeCount > 0) {
            const el = document.getElementById(`block-${blockId}`);
            if (el) {
                const range = selection.getRangeAt(0);
                const preRange = range.cloneRange();
                preRange.selectNodeContents(el);
                preRange.setEnd(range.startContainer, range.startOffset);
                const offset = preRange.toString().length;

                const liveText = el.innerText || "";
                beforeText = liveText.substring(0, offset);
                afterText = liveText.substring(offset);

                el.innerText = beforeText;
            }
        }

        const newId = Date.now();
        const newBlock = {
            id: newId,
            type: 'text',
            variant: currentBlock.variant || 'body1',
            value: afterText,
            ...(currentBlock.listType ? { listType: currentBlock.listType } : {}),
        };
        const updated = [...localBlocks];
        updated[bIdx] = { ...updated[bIdx], value: beforeText };
        updated.splice(bIdx + 1, 0, newBlock);

        isSplittingRef.current = true;
        pendingFocusRef.current = String(newId);
        setLocalBlocks(updated);
        setActiveBlockId(String(newId));

        setTimeout(() => {
            syncToParent(updated);
            setTimeout(() => { isSplittingRef.current = false; }, 100);
        }, 0);
    };

    const handleKeyDown = (e, block) => {
        if (!isSiteBuilder) return;

        if (e.key === 'Backspace' && !e.currentTarget.innerText.trim() && block.type === 'text') {
            e.preventDefault();
            handleDeleteBlock(block.id);
            return;
        }

        if (e.key === 'Enter') {
            if (block.type === 'text') {
                e.preventDefault();
                document.execCommand('insertLineBreak');
                return;
            }
            e.preventDefault();
            handleSplitBlock(block.id);
            return;
        }

        if (e.key === '/' && !e.currentTarget.innerText.trim()) {
            const selection = window.getSelection();
            if (selection && selection.rangeCount > 0) {
                const rect = selection.getRangeAt(0).getBoundingClientRect();
                if (rect) {
                    setPickerState({
                        open: true,
                        anchorEl: {
                            getBoundingClientRect: () => rect,
                            nodeType: 1
                        },
                        blockId: block.id
                    });
                }
            }
        }
    };

    const handleDragEnd = useCallback((result) => {
        if (!result.destination) return;
        const { source, destination } = result;
        if (source.index === destination.index) return;
        setLocalBlocks((prev) => {
            const next = Array.from(prev);
            const [moved] = next.splice(source.index, 1);
            next.splice(destination.index, 0, moved);
            syncToParent(next);
            return next;
        });
    }, [syncToParent]);

    return (
        <Box ref={containerRef} sx={{ width: "100%", position: "relative" }}>
            <DragDropContext onDragEnd={isSiteBuilder && !disableBlockActions ? handleDragEnd : () => { }}>
                <Droppable droppableId={droppableId}>
                    {(droppableProvided) => (
                        <Stack
                            width="100%"
                            mx="auto"
                            spacing={0}
                            ref={droppableProvided.innerRef}
                            {...droppableProvided.droppableProps}
                        >
                            {localBlocks.map((block, index) => (
                                isSiteBuilder && !disableBlockActions ? (
                                    <Box key={String(block.id)}>
                                        <Draggable draggableId={String(block.id)} index={index}>
                                            {(draggableProvided, draggableSnapshot) => (
                                                <Box
                                                    ref={draggableProvided.innerRef}
                                                    {...draggableProvided.draggableProps}
                                                    onClick={() => {
                                                        setActiveBlockId(String(block.id));
                                                        // if (block.uid) {
                                                        //     dispatch({
                                                        //         type: reduxHelper.UPDATE_SITE_BUILDER_SELECTED_CHILD,
                                                        //         payload: block
                                                        //     });
                                                        // }
                                                    }}
                                                    sx={{
                                                        position: 'relative',
                                                        cursor: 'pointer',
                                                        '&:hover .drag-handle': { opacity: 1 },
                                                        ...(draggableSnapshot.isDragging ? {
                                                            bgcolor: 'background.paper',
                                                            boxShadow: 8,
                                                            borderRadius: 1,
                                                            opacity: 0.95,
                                                        } : {})
                                                    }}
                                                >
                                                    {/* Drag handle */}
                                                    <Box
                                                        className="drag-handle"
                                                        {...draggableProvided.dragHandleProps}
                                                        sx={{
                                                            position: 'absolute',
                                                            left: 24,
                                                            top: -10,
                                                            transform: 'none',
                                                            opacity: activeBlockId === String(block.id) ? 1 : 0,
                                                            transition: 'opacity 0.15s',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            cursor: 'grab',
                                                            color: 'text.disabled',
                                                            '&:active': { cursor: 'grabbing' },
                                                            zIndex: 10,
                                                            bgcolor: 'background.paper',
                                                            border: '1px solid',
                                                            borderColor: 'divider',
                                                            borderRadius: 0.75,
                                                            width: 22,
                                                            height: 22,
                                                            p: 0.25,
                                                            '@media (hover: none), (pointer: coarse)': {
                                                                opacity: 1,
                                                            },
                                                        }}
                                                        onClick={(e) => e.stopPropagation()}
                                                    >
                                                        <DragIndicatorRounded sx={{ fontSize: 14 }} />
                                                    </Box>

                                                    <CommonBlock
                                                        block={block}
                                                        isSiteBuilder={isSiteBuilder}
                                                        isActive={activeBlockId === String(block.id)}
                                                        onUpdate={(blockId, updates) => handleUpdateBlock(blockId, updates)}
                                                        onDelete={(blockId) => handleDeleteBlock(blockId)}
                                                        onDuplicate={(blockId) => handleDuplicateBlock(blockId)}
                                                        onSplit={(blockId) => handleSplitBlock(blockId)}
                                                        onKeyDown={(e, b) => handleKeyDown(e, b)}
                                                        onOpenGallery={(blockId, cardId, rect) => {
                                                            if (typeof onOpenGalleryProp === 'function' && onOpenGalleryProp.length > 0) {
                                                                // If a real handler was passed (not our default NO-OP), use it
                                                                onOpenGalleryProp(blockId, cardId, rect);
                                                            } else {
                                                                setEditingBlockIdLocal(String(blockId));
                                                                setEditingCardIdLocal(cardId ? String(cardId) : null);

                                                                if (rect) {
                                                                    setGalleryOpen({ getBoundingClientRect: () => rect });
                                                                } else {
                                                                    setGalleryOpen(true);
                                                                }
                                                            }
                                                        }}

                                                        onTriggerPicker={(rect, blockId) => {
                                                            setPickerState({
                                                                open: true,
                                                                anchorEl: { getBoundingClientRect: () => rect, nodeType: 1 },
                                                                blockId: blockId
                                                            });
                                                        }}
                                                        editingCardId={editingCardId}
                                                        setEditingCardId={setEditingCardId}
                                                        disableBlockActions={disableBlockActions}
                                                        hideBlockDelete={hideBlockDelete}
                                                        nestedDelete={nestedDelete}
                                                    />
                                                </Box>
                                            )}
                                        </Draggable>

                                    </Box>
                                ) : (
                                    <Box
                                        key={block.id}
                                        onClick={() => isSiteBuilder && setActiveBlockId(String(block.id))}
                                        sx={{ cursor: isSiteBuilder ? 'pointer' : 'default' }}
                                    >
                                        <CommonBlock
                                            block={block}
                                            isSiteBuilder={isSiteBuilder}
                                            isActive={activeBlockId === String(block.id)}
                                            onUpdate={(blockId, updates) => handleUpdateBlock(blockId, updates)}
                                            onDelete={(blockId) => handleDeleteBlock(blockId)}
                                            onDuplicate={(blockId) => handleDuplicateBlock(blockId)}
                                            onSplit={(blockId) => handleSplitBlock(blockId)}
                                            onKeyDown={(e, b) => handleKeyDown(e, b)}
                                            onOpenGallery={(blockId, cardId) => onOpenGalleryProp(blockId, cardId)}
                                            onTriggerPicker={(rect, blockId) => {
                                                setPickerState({
                                                    open: true,
                                                    anchorEl: { getBoundingClientRect: () => rect, nodeType: 1 },
                                                    blockId: blockId
                                                });
                                            }}
                                            editingCardId={editingCardId}
                                            setEditingCardId={setEditingCardId}
                                            disableBlockActions={disableBlockActions}
                                            hideBlockDelete={hideBlockDelete}
                                            nestedDelete={nestedDelete}
                                        />
                                    </Box>
                                )
                            ))}
                            {droppableProvided.placeholder}
                            {isSiteBuilder && (
                                <Box sx={{ px: 1.5, mt: 2 }}>
                                    <InlineToolbar
                                        visible={true}
                                        showPrompt={!disableBlockActions && !hideBlockDelete && !hidePrompt && localBlocks.length === 0}
                                        onAddImage={() => {
                                            const nid = handleAddBlock(null, { type: 'image' });
                                            setEditingBlockId && setEditingBlockId(String(nid));
                                            onOpenGalleryProp && onOpenGalleryProp(String(nid));
                                        }}
                                        onAddTable={() => handleAddBlock(null, { type: 'table' })}
                                        onAddMore={(e) => {
                                            const currentTarget = e.currentTarget || e.target;
                                            if (currentTarget) {
                                                // Open after the current click cycle so click-away
                                                // listeners don't immediately close the popover.
                                                requestAnimationFrame(() => {
                                                    setPickerState({
                                                        open: true,
                                                        anchorEl: currentTarget,
                                                        blockId: null
                                                    });
                                                });
                                            }
                                        }}
                                    />
                                </Box>
                            )}
                        </Stack>
                    )}
                </Droppable>
            </DragDropContext>

            <BlockPicker
                open={pickerState.open}
                anchorEl={pickerState.anchorEl}
                onClose={() => setPickerState({ ...pickerState, open: false })}
                onSelect={(opt) => {
                    handleAddBlock(pickerState.blockId, opt);
                    setPickerState({ ...pickerState, open: false });
                }}
            />

            {isSiteBuilder && (
                <ImageGallery
                    showImagePicker={galleryOpen}
                    onReset={() => setGalleryOpen(false)}
                    onAddImage={(imgUrl) => {
                        const bid = editingBlockIdLocal;
                        const cid = editingCardIdLocal;
                        if (!bid) return;

                        handleUpdateBlock(bid, (prev) => {
                            if (prev.type === 'cardgrid' && cid) {
                                return {
                                    ...prev,
                                    cards: (prev.cards || []).map(c =>
                                        String(c.id) === String(cid) ? { ...c, image: imgUrl } : c
                                    )
                                };
                            }
                            return { ...prev, value: imgUrl };
                        });
                        setGalleryOpen(false);
                        setEditingBlockIdLocal(null);
                        setEditingCardIdLocal(null);
                    }}
                />
            )}
        </Box>
    );
};

export default FlexibleEditor;
